import React, { useEffect, useState } from "react";
import GenericTable from "../../../Components/Tables/GenericTable";
import { FilePdfOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getFuelVendors,
  getFuelVendorFuelTypes,
} from "../../../API/FleetManager/FuelVendors";
import { getInitials } from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const hexToRgb = (hex) => {
  const value = parseInt(hex.replace("#", ""), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const getImageFormat = (dataUrl) => {
  const match = /data:image\/(\w+);/.exec(dataUrl);
  if (!match) return "PNG";
  const ext = match[1].toUpperCase();
  return ext === "JPG" ? "JPEG" : ext;
};

const toDataUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const mapVendor = (item, fuelTypes, index) => {
  const prices = {};

  fuelTypes.forEach(({ code }) => {
    const raw = item.fuel_prices?.[code];
    prices[`price_${code}`] =
      raw !== null && raw !== undefined
        ? `${parseFloat(raw).toFixed(2)} ${item.currency || ""}`.trim()
        : "-";
  });

  return {
    key: String(item.id ?? index),
    vendors: item.legal_name || "-",
    avatar: resolveAvatarUrl(item.avatar),
    type: Array.isArray(item.fuel_types_offered)
      ? item.fuel_types_offered.map((f) => f.name).filter(Boolean)
      : [],
    status: capitalize(item.status) || "-",
    ...prices,
  };
};

const FuelVendors = () => {
  const [dataSource, setDataSource] = useState([]);
  const [fuelTypes, setFuelTypes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesRes, vendorsRes] = await Promise.all([
          getFuelVendorFuelTypes(),
          getFuelVendors(),
        ]);

        const types = typesRes?.data?.data;
        const resolvedTypes = Array.isArray(types) ? types : [];

        setFuelTypes(resolvedTypes);

        const vendors = vendorsRes?.data?.data;

        if (
          Math.floor(vendorsRes?.status / 100) === 2 &&
          Array.isArray(vendors)
        ) {
          setDataSource(
            vendors.map((item, index) => mapVendor(item, resolvedTypes, index)),
          );
        } else {
          message.error(
            getErrorMessage(vendorsRes?.data, "Failed to load fuel vendors."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel vendors."),
        );
      }
    };

    fetchData();
  }, []);

  const uniqueTypes = [...new Set(dataSource.flatMap((item) => item.type))];

  const typeFilters = uniqueTypes.map((t) => ({
    text: t,
    value: t,
  }));

  const uniqueStatuses = [
    ...new Set(dataSource.map((item) => item.status).filter((v) => v !== "-")),
  ];

  const statusFilters = uniqueStatuses.map((status) => ({
    text: status,
    value: status,
  }));

  const priceSorter = (a, b, key) => {
    const parsePrice = (val) => parseFloat(val?.replace(/[^0-9.-]+/g, "")) || 0;
    return parsePrice(a[key]) - parsePrice(b[key]);
  };

  const columns = [
    {
      title: "Vendors",
      dataIndex: "vendors",
      key: "vendors",
      render: (text, record) =>
        record.avatar ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={record.avatar}
              alt={text}
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
            <span style={{ fontWeight: 500 }}>{text}</span>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
                background: getAvatarColor(text),
                flexShrink: 0,
              }}
            >
              {getInitials(text)}
            </span>
            <span style={{ fontWeight: 500 }}>{text}</span>
          </div>
        ),
      enableSearch: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (types) => {
        if (!Array.isArray(types) || types.length === 0) return "-";

        return (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {types.map((t) => (
              <span key={t} className="type-badge">
                {t}
              </span>
            ))}
          </div>
        );
      },
      filters: typeFilters,
      onFilter: (value, record) => record.type?.includes(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",

      filters: statusFilters,

      onFilter: (value, record) => record.status === value,

      render: (status) => (
        <span className={`status-tag ${status.toLowerCase()}`}>{status}</span>
      ),
    },
    ...fuelTypes.map(({ code, name }) => ({
      title: name,
      dataIndex: `price_${code}`,
      key: `price_${code}`,
      sorter: (a, b) => priceSorter(a, b, `price_${code}`),
    })),
  ];

  const handleExport = async () => {
    if (dataSource.length === 0) {
      message.error("No fuel vendors to export.");
      return;
    }

    const avatarDataUrls = await Promise.all(
      dataSource.map((row) => (row.avatar ? toDataUrl(row.avatar) : null)),
    );

    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(14);
    doc.text("Fuel Vendors", 14, 15);

    const head = [
      "Vendors",
      "Type",
      "Status",
      ...fuelTypes.map(({ name }) => name),
    ];

    const body = dataSource.map((row) => [
      row.vendors,
      Array.isArray(row.type) && row.type.length > 0
        ? row.type.join(", ")
        : "-",
      row.status,
      ...fuelTypes.map(({ code }) => row[`price_${code}`]),
    ]);

    autoTable(doc, {
      head: [head],
      body,
      startY: 20,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229] },
      columnStyles: { 0: { cellPadding: { top: 3, right: 3, bottom: 3, left: 12 } } },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 0) return;

        const row = dataSource[data.row.index];
        if (!row) return;

        const size = 6;
        const cx = data.cell.x + 3;
        const cy = data.cell.y + data.cell.height / 2 - size / 2;
        const dataUrl = avatarDataUrls[data.row.index];

        if (dataUrl) {
          try {
            doc.addImage(dataUrl, getImageFormat(dataUrl), cx, cy, size, size);
            return;
          } catch {
            // fall through to initials fallback
          }
        }

        const [r, g, b] = hexToRgb(getAvatarColor(row.vendors));

        doc.setFillColor(r, g, b);
        doc.circle(cx + size / 2, cy + size / 2, size / 2, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(6);
        doc.text((row.vendors || "?").charAt(0).toUpperCase(), cx + size / 2, cy + size / 2 + 1.5, {
          align: "center",
        });
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
      },
    });

    doc.save("fuel-vendors.pdf");
  };

  return (
    <div>
      <div
        className="export-btn"
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="primary"
          icon={<FilePdfOutlined />}
          className="export-pdf-btn"
          onClick={handleExport}
        >
          Export PDF
        </Button>
      </div>

      <div className="fuel-table">
        <GenericTable
          title="Fuel Vendors"
          dataSource={dataSource}
          columns={columns}
        />
      </div>
    </div>
  );
};

export default FuelVendors;
